import React, { useState, useEffect } from "react";

const G = "#0077B5", GRN = "#1D9E75", B = "#E1E9EE", HONEY = "#F5A623";
const FONT = "-apple-system,'Segoe UI',Helvetica,sans-serif";
const SERIF = "'Playfair Display',Georgia,serif";

const STATUS_BADGE = {
  active:   { bg: "#ECFDF5", color: "#065F46", label: "Active", dot: GRN },
  paused:   { bg: "#FEF3C7", color: "#92400E", label: "Paused", dot: "#F59E0B" },
  override: { bg: "#EBF5FB", color: "#005885", label: "Override this week", dot: G },
};

const HIST_STATUS = {
  draft:     { bg: "#F3F4F6", color: "#888", label: "Draft" },
  scheduled: { bg: "#EBF5FB", color: G, label: "Scheduled" },
  posted:    { bg: "#ECFDF5", color: GRN, label: "Published" },
  muted:     { bg: "#FEF3C7", color: "#92400E", label: "Muted" },
};

const VOICE_OPTIONS = [
  { value: "me",      label: "Me",      desc: "I / me / my" },
  { value: "we",      label: "We",      desc: "We / us / our" },
  { value: "company", label: "Company", desc: "Company name / third person" },
];

// Map voice_identity UI value to plug_type questions for shameless generate
const SHAMELESS_QUESTIONS = [
  "What do you do in one sentence?",
  "Who is your ideal client?",
  "What makes you different from everyone else?",
];

/**
 * Authority Presence Card — Phase 2A (real data wiring)
 *
 * Props:
 *   user      - user object from NectarApp
 *   dashState - dashboard-state response (has default_plug_*, voice_identity, has_plug_configured, upcoming_posts)
 *   API_BASE  - API base URL
 *   isTrialing - boolean
 */
export default function AuthorityPresenceCard({ user, dashState, API_BASE, isTrialing }) {
  const plan = (user?.plan || "").toLowerCase();
  const isPremium = plan === "authority" || plan === "authority_pro" || plan === "team" || plan === "agency" || plan === "authority_team" || plan === "authority_team_member" || plan === "pro_team" || plan === "pro_team_member" || isTrialing;
  const isWorkspaceAdmin = !!user?.is_workspace_admin;
  const isWorkspaceMemberOnly = user?.is_workspace_member && !user?.is_workspace_admin;

  // Derive initial state from real data
  const isConfigured = dashState?.has_plug_configured || false;
  const savedApStatus = dashState?.authority_presence_status || (isConfigured ? "active" : null);

  const [setupState, setSetupState] = useState(isConfigured ? "active" : "pre");
  const [dismissed, setDismissed] = useState(() => {
    const saved = localStorage.getItem("authority_presence_remind_later");
    if (!saved) return false;
    const expiry = parseInt(saved, 10);
    if (Date.now() > expiry) { localStorage.removeItem("authority_presence_remind_later"); return false; }
    return true;
  });

  // Onboarding form fields — pre-fill from dashState if editing existing
  const savedAnswers = dashState?.default_plug_answers || {};
  const [whatYouDo, setWhatYouDo] = useState(dashState?.default_offering || "");
  const [whoYouHelp, setWhoYouHelp] = useState(savedAnswers.who_you_help || savedAnswers[SHAMELESS_QUESTIONS[1]] || "");
  const [whatsDifferent, setWhatsDifferent] = useState(savedAnswers.whats_different || savedAnswers[SHAMELESS_QUESTIONS[2]] || "");
  const [voiceIdentity, setVoiceIdentity] = useState(dashState?.voice_identity || "me");

  // Preview state
  const [previewCaption, setPreviewCaption] = useState("");
  const [previewAngle, setPreviewAngle] = useState("Shameless Plug");
  const [previewCompliance, setPreviewCompliance] = useState(null);
  const [generateError, setGenerateError] = useState("");

  // Active state
  const [apStatus, setApStatus] = useState(savedApStatus === "paused" ? "paused" : "active");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  // Plug history (real data from /plug/list)
  const [history, setHistory] = useState([]);
  const [histLoaded, setHistLoaded] = useState(false);

  // This week's scheduled plug from dashboard upcoming_posts
  const thisWeekPlug = (dashState?.upcoming_posts || []).find(p => p.is_plug);

  // Load plug history when entering active state
  useEffect(() => {
    if (setupState === "active" && isPremium && !histLoaded && API_BASE) {
      fetch(`${API_BASE}/app/api/user/plug/list`, { credentials: "include" })
        .then(r => r.ok ? r.json() : { plug_posts: [] })
        .then(d => { setHistory((d.plug_posts || []).slice(0, 5)); setHistLoaded(true); })
        .catch(() => setHistLoaded(true));
    }
  }, [setupState, isPremium, histLoaded, API_BASE]);

  // ── Save setup via /plug/save-defaults ──
  async function handleSaveAndGenerate() {
    setSaving(true);
    setSaveMsg("");
    setGenerateError("");

    const existingMeta = (dashState?.default_plug_answers || {})._meta || {};
    const answers = {
      [SHAMELESS_QUESTIONS[0]]: whatYouDo,
      [SHAMELESS_QUESTIONS[1]]: whoYouHelp,
      [SHAMELESS_QUESTIONS[2]]: whatsDifferent,
      who_you_help: whoYouHelp,
      whats_different: whatsDifferent,
      voice_identity: voiceIdentity,
      _meta: { ...existingMeta, authority_presence_status: "active" },
    };

    try {
      // Save defaults
      const saveResp = await fetch(`${API_BASE}/app/api/user/plug/save-defaults`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          default_plug_type: "shameless",
          default_plug_tone: user?.tone || "professional",
          default_offering: whatYouDo,
          default_offering_link: dashState?.default_offering_link || null,
          default_plug_answers: answers,
        }),
      });
      if (!saveResp.ok) { setGenerateError("Failed to save profile."); setSaving(false); return; }

      setSetupState("generating");

      // Generate preview via /plug/generate
      const genResp = await fetch(`${API_BASE}/app/api/user/plug/generate`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plug_type: "shameless",
          tone: (user?.tone || "professional").toLowerCase(),
          offering: whatYouDo,
          offering_link: dashState?.default_offering_link || null,
          answers: answers,
        }),
      });
      const genData = await genResp.json();

      if (genData.caption) {
        setPreviewCaption(genData.caption);
        setPreviewCompliance(genData.compliance_check || null);
        setPreviewAngle("Shameless Plug");
        setSetupState("preview");
      } else {
        setGenerateError(genData.error || "Generation failed. Try again.");
        setSetupState("onboarding");
      }
    } catch (e) {
      setGenerateError("Connection error. Try again.");
      setSetupState("onboarding");
    }
    setSaving(false);
  }

  // ── Regenerate preview ──
  async function handleRegenerate() {
    setSetupState("generating");
    setGenerateError("");
    try {
      const genResp = await fetch(`${API_BASE}/app/api/user/plug/generate`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plug_type: "shameless",
          tone: (user?.tone || "professional").toLowerCase(),
          offering: whatYouDo,
          offering_link: dashState?.default_offering_link || null,
          answers: {
            [SHAMELESS_QUESTIONS[0]]: whatYouDo,
            [SHAMELESS_QUESTIONS[1]]: whoYouHelp,
            [SHAMELESS_QUESTIONS[2]]: whatsDifferent,
          },
        }),
      });
      const genData = await genResp.json();
      if (genData.caption) {
        setPreviewCaption(genData.caption);
        setPreviewCompliance(genData.compliance_check || null);
        setSetupState("preview");
      } else {
        setGenerateError(genData.error || "Generation failed.");
        setSetupState("preview");
      }
    } catch {
      setGenerateError("Connection error.");
      setSetupState("preview");
    }
  }

  // ── Activate: transition to active state ──
  function handleActivate() {
    setSetupState("active");
    setHistLoaded(false); // reload history
    setSaveMsg("Authority Presence activated.");
    setTimeout(() => setSaveMsg(""), 3000);
  }

  // ── Workspace admin: redirect to workspace settings ──
  if (isWorkspaceAdmin) {
    return (
      <div style={{ background: "#FFFFFF", border: `1px solid ${B}`, borderRadius: 14, padding: "40px 32px", textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 16 }}>🎯</div>
        <div style={{ fontFamily: SERIF, fontSize: 22, marginBottom: 12 }}>
          Your team's Authority Presence is managed in Workspace Settings.
        </div>
        <div style={{ fontSize: 15, color: "#666", lineHeight: 1.7, marginBottom: 28, maxWidth: 480, margin: "0 auto 28px", fontFamily: FONT }}>
          As a workspace admin, your content is created at the team level and personalized for each member.
        </div>
        <a href="#/team" style={{ display: "inline-block", padding: "14px 32px", fontFamily: SERIF, fontSize: 16, backgroundColor: G, color: "#fff", borderRadius: 10, textDecoration: "none" }}>Go to Workspace →</a>
      </div>
    );
  }

  // ── Workspace member: team-managed ──
  if (isWorkspaceMemberOnly) {
    return (
      <div style={{ background: "#FFFFFF", border: `1px solid ${B}`, borderRadius: 14, padding: "40px 32px", textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 16 }}>🐝</div>
        <div style={{ fontFamily: SERIF, fontSize: 22, marginBottom: 12 }}>
          Your content is managed by your team admin.
        </div>
        <div style={{ fontSize: 15, color: "#666", lineHeight: 1.7, marginBottom: 28, maxWidth: 480, margin: "0 auto 28px", fontFamily: FONT }}>
          You can personalize your voice and approve your content from your workspace dashboard.
        </div>
        <a href="#/team" style={{ display: "inline-block", padding: "14px 32px", fontFamily: SERIF, fontSize: 16, backgroundColor: G, color: "#fff", borderRadius: 10, textDecoration: "none" }}>Go to Workspace →</a>
      </div>
    );
  }

  // ── Locked state (Free/Presence) ──
  if (!isPremium) {
    return (
      <div style={{ background: "#FFFFFF", border: `1px solid ${B}`, borderRadius: 14, padding: "40px 32px", textAlign: "center" }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🔒</div>
        <div style={{ fontFamily: SERIF, fontSize: 24, marginBottom: 12 }}>
          Authority Presence is an <em style={{ color: G }}>Authority exclusive.</em>
        </div>
        <div style={{ fontSize: 15, color: "#666", lineHeight: 1.7, marginBottom: 28, maxWidth: 480, margin: "0 auto 28px", fontFamily: FONT }}>
          Stay visible even when life gets busy. Authority Presence writes and publishes a weekly post in your voice, automatically.
        </div>
        <button style={{ background: "#F59E0B", color: "#fff", border: "none", borderRadius: 10, padding: "14px 32px", fontFamily: SERIF, fontSize: 16, cursor: "pointer" }}>
          Upgrade to Authority →
        </button>
      </div>
    );
  }

  // ── Active state ──
  if (setupState === "active") {
    const badge = STATUS_BADGE[apStatus] || STATUS_BADGE.active;
    return (
      <div style={{ background: "#FFFFFF", border: `1px solid ${B}`, borderRadius: 14, overflow: "hidden" }}>
        {/* Header */}
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${B}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ fontFamily: SERIF, fontSize: 22 }}>Authority Presence</div>
            <span style={{ background: badge.bg, color: badge.color, borderRadius: 20, padding: "4px 14px", fontSize: 12, fontWeight: 600, fontFamily: FONT, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: badge.dot, display: "inline-block" }} />
              {badge.label}
            </span>
          </div>
          <button onClick={async () => {
            const newStatus = apStatus === "active" ? "paused" : "active";
            const currentAnswers = dashState?.default_plug_answers || {};
            await fetch(`${API_BASE}/app/api/user/plug/save-defaults`, {
              method: "POST", credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ default_plug_answers: { ...currentAnswers, _meta: { ...(currentAnswers._meta || {}), authority_presence_status: newStatus } } }),
            });
            setApStatus(newStatus);
            setSaveMsg(newStatus === "paused" ? "Authority Presence paused." : "Authority Presence resumed.");
            setTimeout(() => setSaveMsg(""), 3000);
          }} style={{ background: "transparent", color: "#888", border: `1px solid ${B}`, borderRadius: 8, padding: "6px 16px", fontSize: 13, fontFamily: FONT, cursor: "pointer" }}>
            {apStatus === "active" ? "Pause" : "Resume"}
          </button>
        </div>

        {saveMsg && (
          <div style={{ padding: "10px 24px", background: "#ECFDF5", fontSize: 13, color: GRN, fontFamily: FONT }}>✓ {saveMsg}</div>
        )}

        {/* This week */}
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${B}` }}>
          <div style={{ fontSize: 11, color: "#999", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 12, fontFamily: FONT }}>This Week</div>
          {thisWeekPlug ? (
            <>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 12, background: "#EBF5FB", color: G, borderRadius: 4, padding: "2px 10px", fontFamily: FONT, fontWeight: 600 }}>
                  {thisWeekPlug.is_plug ? "Authority Presence" : "Override"}
                </span>
                <span style={{ fontSize: 12, color: "#888", fontFamily: FONT }}>{thisWeekPlug.scheduled_time}</span>
              </div>
              <div style={{ fontSize: 14, color: "#444", fontFamily: FONT, lineHeight: 1.6 }}>
                "{(thisWeekPlug.caption || "").slice(0, 120)}{(thisWeekPlug.caption || "").length > 120 ? "..." : ""}"
              </div>
            </>
          ) : (
            <div style={{ fontSize: 14, color: "#888", fontFamily: FONT }}>No plug scheduled yet this week.</div>
          )}
        </div>

        {/* Actions */}
        <div style={{ padding: "16px 24px", borderBottom: `1px solid ${B}`, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button onClick={() => setSetupState("onboarding")} style={{ background: "transparent", color: G, border: `1px solid ${G}`, borderRadius: 8, padding: "8px 18px", fontSize: 13, fontFamily: FONT, fontWeight: 600, cursor: "pointer" }}>Edit Profile</button>
          <button onClick={() => { window.location.hash = "/weekly-override"; }} style={{ background: G, color: "#fff", border: "none", borderRadius: 8, padding: "8px 18px", fontSize: 13, fontFamily: FONT, fontWeight: 600, cursor: "pointer" }}>Create Campaign</button>
        </div>

        {/* History */}
        <div style={{ padding: "20px 24px" }}>
          <div style={{ fontSize: 11, color: "#999", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 12, fontFamily: FONT }}>Recent Plug History</div>
          {history.length === 0 && histLoaded && (
            <div style={{ fontSize: 13, color: "#888", fontFamily: FONT }}>No plug history yet.</div>
          )}
          {history.map((h, i) => {
            const hs = HIST_STATUS[h.status] || HIST_STATUS.draft;
            const dateStr = h.created_at ? new Date(h.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";
            return (
              <div key={h.id || i} style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "10px 0", borderBottom: i < history.length - 1 ? `1px solid ${B}` : "none" }}>
                <div style={{ fontSize: 13, color: "#888", fontFamily: FONT, minWidth: 60, flexShrink: 0 }}>{dateStr}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: "#444", fontFamily: FONT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {((h.generated_caption || "").slice(0, 80))}{(h.generated_caption || "").length > 80 ? "..." : ""}
                  </div>
                </div>
                <span style={{ fontSize: 11, background: hs.bg, color: hs.color, borderRadius: 4, padding: "2px 8px", fontFamily: FONT, fontWeight: 600, flexShrink: 0 }}>{hs.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Preview screen ──
  if (setupState === "preview") {
    const hasBlocks = previewCompliance?.has_blocks;
    const warnCount = previewCompliance?.warn_count || 0;
    const safetyLabel = hasBlocks ? "Blocked" : warnCount > 0 ? `${warnCount} warning${warnCount > 1 ? "s" : ""}` : "Clean";
    const safetyColor = hasBlocks ? "#DC2626" : warnCount > 0 ? "#F59E0B" : GRN;
    return (
      <div style={{ background: "#FFFFFF", border: `1px solid ${B}`, borderRadius: 14, padding: "32px", maxWidth: 600 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <div style={{ fontFamily: SERIF, fontSize: 22 }}>Authority Presence Preview</div>
          <span style={{ fontSize: 13, color: "#999", fontFamily: FONT }}>2/2</span>
        </div>
        <div style={{ fontSize: 14, color: "#666", fontFamily: FONT, marginBottom: 24, lineHeight: 1.6 }}>
          Here's a sample of what your weekly post will look like. Each week uses a different angle from your identity.
        </div>

        {/* LinkedIn-style preview */}
        <div style={{ border: `1px solid ${B}`, borderRadius: 10, padding: "20px", marginBottom: 20, background: "#FAFAFA" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", background: G, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ color: "#fff", fontWeight: 600, fontSize: 16 }}>{(user?.first_name || "Y")[0]}</span>
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, fontFamily: FONT }}>{user?.linkedin_name || user?.first_name || "Your Name"}</div>
              <div style={{ fontSize: 12, color: "#888", fontFamily: FONT }}>{user?.headline || "Your headline"}</div>
            </div>
          </div>
          <div style={{ fontSize: 14, color: "#333", fontFamily: FONT, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
            {previewCaption}
          </div>
        </div>

        <div style={{ display: "flex", gap: 16, marginBottom: 24, fontFamily: FONT, fontSize: 13, color: "#666", flexWrap: "wrap" }}>
          <span>Compliance: <strong style={{ color: safetyColor }}>{safetyLabel}</strong></span>
          <span>Angle: <strong>{previewAngle}</strong></span>
          <span>Voice: <strong>{VOICE_OPTIONS.find(v => v.value === voiceIdentity)?.desc || "I/me/my"}</strong></span>
        </div>

        {generateError && (
          <div style={{ fontSize: 13, color: "#DC2626", fontFamily: FONT, marginBottom: 16 }}>{generateError}</div>
        )}

        <div style={{ fontSize: 13, color: "#888", fontFamily: FONT, marginBottom: 20 }}>
          This will become your tone anchor. Future posts will match this style while covering different topics.
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button onClick={() => setSetupState("onboarding")} style={{ background: "transparent", color: "#888", border: `1px solid ${B}`, borderRadius: 10, padding: "12px 22px", fontFamily: FONT, fontSize: 14, cursor: "pointer" }}>
            Edit
          </button>
          <button onClick={handleRegenerate} style={{ background: "transparent", color: G, border: `1px solid ${G}`, borderRadius: 10, padding: "12px 22px", fontFamily: FONT, fontSize: 14, cursor: "pointer" }}>
            Regenerate
          </button>
          <button onClick={handleActivate} disabled={hasBlocks} style={{ background: hasBlocks ? "#ccc" : GRN, color: "#fff", border: "none", borderRadius: 10, padding: "12px 28px", fontFamily: FONT, fontSize: 15, fontWeight: 600, cursor: hasBlocks ? "not-allowed" : "pointer" }}>
            Activate My Presence
          </button>
        </div>
      </div>
    );
  }

  // ── Generating state ──
  if (setupState === "generating") {
    return (
      <div style={{ background: "#FFFFFF", border: `1px solid ${B}`, borderRadius: 14, padding: "60px 32px", textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 16 }}>🐝</div>
        <div style={{ fontFamily: SERIF, fontSize: 20, marginBottom: 8 }}>Building your Authority Presence...</div>
        <div style={{ fontSize: 13, color: "#999", fontFamily: FONT }}>Creating your identity and a sample post. Just a moment.</div>
      </div>
    );
  }

  // ── Onboarding flow ──
  if (setupState === "onboarding") {
    const canGenerate = whatYouDo.trim() && whoYouHelp.trim() && !saving;
    return (
      <div style={{ background: "#FFFFFF", border: `1px solid ${B}`, borderRadius: 14, padding: "32px", maxWidth: 600 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <div style={{ fontFamily: SERIF, fontSize: 22 }}>Authority Presence Setup</div>
          <span style={{ fontSize: 13, color: "#999", fontFamily: FONT }}>1/2</span>
        </div>
        <div style={{ fontSize: 14, color: "#666", fontFamily: FONT, marginBottom: 28, lineHeight: 1.6 }}>
          Answer a few quick questions so we can build your ongoing presence. Takes about 60 seconds.
        </div>

        {generateError && (
          <div style={{ fontSize: 13, color: "#DC2626", fontFamily: FONT, marginBottom: 16, padding: "8px 12px", background: "#FEF2F2", borderRadius: 6 }}>{generateError}</div>
        )}

        {/* Q1 */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#444", marginBottom: 6, fontFamily: FONT }}>What do you do?</label>
          <textarea value={whatYouDo} onChange={e => setWhatYouDo(e.target.value)} rows={2} maxLength={300} placeholder="e.g. I help mid-market SaaS teams build repeatable sales processes"
            style={{ width: "100%", border: `1px solid ${B}`, borderRadius: 8, padding: "10px 14px", fontFamily: FONT, fontSize: 14, resize: "vertical", outline: "none", boxSizing: "border-box" }} />
        </div>

        {/* Q2 */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#444", marginBottom: 6, fontFamily: FONT }}>Who do you help?</label>
          <textarea value={whoYouHelp} onChange={e => setWhoYouHelp(e.target.value)} rows={2} maxLength={300} placeholder="e.g. B2B SaaS founders doing $1-10M ARR who are stuck selling themselves"
            style={{ width: "100%", border: `1px solid ${B}`, borderRadius: 8, padding: "10px 14px", fontFamily: FONT, fontSize: 14, resize: "vertical", outline: "none", boxSizing: "border-box" }} />
        </div>

        {/* Q3 (optional) */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#444", marginBottom: 6, fontFamily: FONT }}>What makes you different? <span style={{ fontWeight: 400, color: "#999" }}>(optional)</span></label>
          <textarea value={whatsDifferent} onChange={e => setWhatsDifferent(e.target.value)} rows={2} maxLength={300} placeholder="e.g. I've scaled 50+ teams without expensive consultants"
            style={{ width: "100%", border: `1px solid ${B}`, borderRadius: 8, padding: "10px 14px", fontFamily: FONT, fontSize: 14, resize: "vertical", outline: "none", boxSizing: "border-box" }} />
        </div>

        {/* Voice identity */}
        <div style={{ marginBottom: 28 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#444", marginBottom: 10, fontFamily: FONT }}>Voice identity</label>
          <div style={{ display: "flex", gap: 10 }}>
            {VOICE_OPTIONS.map(v => (
              <button key={v.value} onClick={() => setVoiceIdentity(v.value)}
                style={{ flex: 1, padding: "12px", borderRadius: 10, border: `2px solid ${voiceIdentity === v.value ? GRN : B}`, background: voiceIdentity === v.value ? GRN + "15" : "#fff", cursor: "pointer", textAlign: "center" }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: voiceIdentity === v.value ? GRN : "#444", fontFamily: FONT }}>{v.label}</div>
                <div style={{ fontSize: 12, color: "#888", fontFamily: FONT, marginTop: 2 }}>{v.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => setSetupState(isConfigured ? "active" : "pre")} style={{ background: "transparent", color: "#888", border: `1px solid ${B}`, borderRadius: 10, padding: "12px 22px", fontFamily: FONT, fontSize: 14, cursor: "pointer" }}>
            ← Back
          </button>
          <button disabled={!canGenerate} onClick={handleSaveAndGenerate}
            style={{ background: canGenerate ? G : "#ccc", color: "#fff", border: "none", borderRadius: 10, padding: "12px 28px", fontFamily: FONT, fontSize: 15, fontWeight: 600, cursor: canGenerate ? "pointer" : "not-allowed" }}>
            {saving ? "Saving..." : "Save & Generate Preview"}
          </button>
        </div>
      </div>
    );
  }

  // ── Pre-setup state (default) ──
  if (dismissed) return null;

  return (
    <div style={{ background: "#FFFBEB", border: `2px solid ${HONEY}`, borderRadius: 14, padding: "28px 32px", marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ fontFamily: SERIF, fontSize: 22 }}>Build Your Authority Presence ⭐</div>
      </div>
      <div style={{ fontSize: 15, color: "#666", fontFamily: FONT, lineHeight: 1.7, marginBottom: 20 }}>
        Stay visible even when life gets busy.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 24, fontFamily: FONT, fontSize: 14, color: "#555" }}>
        <div>✓ Uses your voice</div>
        <div>✓ Learns your style</div>
        <div>✓ Keeps your presence active</div>
        <div>✓ Override anytime</div>
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <button onClick={() => setSetupState("onboarding")} style={{ background: HONEY, color: "#fff", border: "none", borderRadius: 10, padding: "13px 28px", fontFamily: FONT, fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
          Set Up Now
        </button>
        <button onClick={() => { setDismissed(true); localStorage.setItem("authority_presence_remind_later", String(Date.now() + 7 * 24 * 60 * 60 * 1000)); }} style={{ background: "transparent", color: "#888", border: `1px solid ${B}`, borderRadius: 10, padding: "13px 28px", fontFamily: FONT, fontSize: 15, cursor: "pointer" }}>
          Later
        </button>
      </div>
    </div>
  );
}
